import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type Store from '@ember-data/store';
import dayjs from 'dayjs';
import { type Task, task } from 'ember-concurrency';
import type { NetRevenueDashboard } from 'lumen/models';
import type { Transition } from 'lumen/services/transition';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  COMPARISON_MODES,
  type ComparisonMode,
  DATE_FORMAT,
} from 'lumen/utils/constants/net-revenue-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import { defaultComparisonFromDate } from 'lumen/utils/net-revenue/date-pickers';

export interface NetRevenueDashboardArgs {
  comparisonFromDate: string;
  comparisonMode: ComparisonMode;
  fromDate: string;
  toDate: string;
}

export interface NetRevenueModel {
  dashboard: Task<NetRevenueDashboard | null, [NetRevenueDashboardArgs]>;
}

export type NetRevenueQueryParams = {
  comparisonFromDate: string | null;
  comparisonMode: ComparisonMode | null;
  fromDate: string | null;
  toDate: string | null;
};

@protectedRoute({
  features: 'app.net_revenue.view',
  featureToggles: FEATURE_TOGGLES.BI_NET_REVENUE,
})
export default class HotelNetRevenueRoute extends Route {
  queryParams = {
    comparisonFromDate: { refreshModel: true },
    comparisonMode: { refreshModel: true },
    fromDate: { refreshModel: true },
    toDate: { refreshModel: true },
  };

  @service declare store: Store;
  @service declare transition: Services['transition'];

  getDashboardTask: Task<
    NetRevenueDashboard | null,
    [NetRevenueDashboardArgs]
  > = task(
    { restartable: true },
    async (args: NetRevenueDashboardArgs) =>
      await this.store.queryRecord<NetRevenueDashboard>(
        'net-revenue/dashboard',
        {
          from_date_range_start: args.fromDate,
          from_date_range_end: args.toDate,
          reference_periods: [args.comparisonMode],
          custom_from_date:
            args.comparisonMode === COMPARISON_MODES.CUSTOM
              ? args.comparisonFromDate
              : undefined,
        },
      ),
  );

  beforeModel(transition: Transition): void {
    super.beforeModel(transition);
    this.transition.validateNetRevenueQueryParams(
      transition,
      this.paramsFor(this.routeName) as NetRevenueQueryParams,
    );
  }

  model(params: NetRevenueQueryParams): NetRevenueModel {
    const model: NetRevenueModel = { dashboard: this.getDashboardTask };

    const today = dayjs();
    const fromDate =
      params.fromDate ?? today.startOf('month').format(DATE_FORMAT);
    const toDate = params.toDate ?? today.endOf('month').format(DATE_FORMAT);
    const comparisonMode: ComparisonMode =
      params.comparisonMode ?? COMPARISON_MODES.SWLY;
    const comparisonFromDate =
      params.comparisonFromDate ??
      defaultComparisonFromDate({ comparisonMode, fromDate, toDate });

    void model.dashboard.perform({
      comparisonFromDate,
      comparisonMode,
      fromDate,
      toDate,
    });

    return model;
  }
}
